import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, deviceUuid, type Thread } from "../api";
import { useApiData } from "../hooks";
import { useApp } from "../store";
import { ErrorNote, Header, Loading, Note } from "../ui";

/**
 * One conversation: the thread, the composer, and the two sync behaviours that hang off it.
 *
 * Opening the thread marks it read (read on one device marks read on all). The in-progress text is the
 * only device-local state in the product; on leaving it is saved as a draft and pushed, which is the
 * design's save-on-exit rule rather than live mirroring.
 */
export function ThreadPage(): JSX.Element {
  const { conversationId = "" } = useParams();
  const { numbers } = useApp();
  const { data, error, reload } = useApiData<Thread>(`/api/v1/conversations/${conversationId}`);
  const [text, setText] = useState("");
  const [sendError, setSendError] = useState<unknown>(null);
  const loadedDraft = useRef(false);
  const latestText = useRef("");
  const bottom = useRef<HTMLDivElement | null>(null);

  latestText.current = text;

  useEffect(() => {
    if (data && !loadedDraft.current) {
      loadedDraft.current = true;
      setText(data.conversation.draftText ?? "");
    }
  }, [data]);

  // Opening the thread is what marks it read.
  useEffect(() => {
    if (conversationId) {
      api.post(`/api/v1/conversations/${conversationId}/read`, {}).catch(() => undefined);
    }
  }, [conversationId]);

  // Save-on-exit: the draft is pushed when the view is left, not on every keystroke.
  useEffect(
    () => () => {
      const pending = latestText.current;
      api
        .put(`/api/v1/conversations/${conversationId}/draft`, {
          text: pending,
          deviceUuid: deviceUuid()
        })
        .catch(() => undefined);
    },
    [conversationId]
  );

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages.length]);

  async function send(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!data || !text.trim()) {
      return;
    }
    setSendError(null);
    try {
      await api.post("/api/v1/messages", {
        numberId: data.conversation.numberId,
        to: data.conversation.counterparty,
        text
      });
      setText("");
      reload();
    } catch (caught) {
      setSendError(caught);
    }
  }

  async function call(): Promise<void> {
    if (!data) {
      return;
    }
    try {
      await api.post("/api/v1/call-sessions", {
        numberId: data.conversation.numberId,
        to: data.conversation.counterparty
      });
    } catch (caught) {
      setSendError(caught);
    }
  }

  const number = numbers.find((candidate) => candidate.id === data?.conversation.numberId);

  return (
    <>
      <Header
        title={data?.conversation.contactLabel ?? "Conversation"}
        back="/conversations"
        action={
          <button className="action link" type="button" title="Call" onClick={call}>
            {"\u260E"}
          </button>
        }
      />
      <div className="content">
        {data === null ? (
          <Loading />
        ) : (
          <>
            <Note>
              Conversation on {number?.e164 ?? data.conversation.numberId}
              {number && number.state !== "ACTIVE" ? ` \u00B7 ${number.state.toLowerCase()}` : ""}
            </Note>
            <ErrorNote error={error} />
            <ErrorNote error={sendError} />
            <div className="thread">
              {data.messages.map((message) => (
                <div
                  key={message.id}
                  className={message.direction === "INBOUND" ? "bubble in" : "bubble out"}
                >
                  {message.messageText}
                  <span className="time">
                    {new Date(message.occurredAt).toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                    {message.direction === "OUTBOUND" && message.messageStatus
                      ? ` \u00B7 ${message.messageStatus.toLowerCase()}`
                      : ""}
                    {message.direction === "INBOUND" && message.readAt ? " \u00B7 read" : ""}
                  </span>
                </div>
              ))}
              <div ref={bottom} />
            </div>
            {data.messages.length === 0 ? <Note>No messages in this conversation yet.</Note> : null}
          </>
        )}
      </div>
      <form className="composer" onSubmit={send}>
        <input
          type="text"
          placeholder="Message"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <button className="send" type="submit" title="Send">
          {"\u21B5"}
        </button>
      </form>
    </>
  );
}
